/**
 * i18n/index.ts
 *
 * Простой слой без внешних зависимостей — единый источник строк: locales/*.json.
 * Язык хранится в localStorage['selectedLanguage']. Поддержка EN / RU / ZH.
 */

import enTranslations from './locales/en.json'
import ru from './locales/ru.json'
import zh from './locales/zh.json'

export type SupportedLang = 'en' | 'ru' | 'zh'

export const LANGUAGES: Record<SupportedLang, string> = {
  en: 'English',
  ru: 'Русский',
  zh: '中文',
}

/** Dot-separated paths to leaf string values in en.json (e.g. `common.confirm`). */
type Leaves<T, P extends string = ''> = T extends string
  ? P extends ''
    ? never
    : P
  : {
      [K in keyof T & string]: Leaves<T[K], P extends '' ? K : `${P}.${K}`>
    }[keyof T & string]

export type TranslationKey = Leaves<typeof enTranslations>

const locales: Record<SupportedLang, Record<string, unknown>> = {
  en: enTranslations,
  ru,
  zh,
}

const STORAGE_KEY = 'selectedLanguage'

export function getSavedLang(): SupportedLang {
  if (typeof window === 'undefined') return 'en'
  const raw = localStorage.getItem(STORAGE_KEY) ?? 'en'
  if (raw.startsWith('ru')) return 'ru'
  if (raw.startsWith('zh')) return 'zh'
  return 'en'
}

export function setLang(lang: SupportedLang): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(STORAGE_KEY, lang)
  window.dispatchEvent(new Event('language-changed'))
}

function getNestedValue(obj: Record<string, unknown>, path: string): string {
  const result = path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object') return (acc as Record<string, unknown>)[key]
    return undefined
  }, obj)
  return typeof result === 'string' ? result : path
}

export function translate(key: TranslationKey, lang?: SupportedLang): string {
  const l = lang ?? getSavedLang()
  const locale = locales[l] ?? locales.en
  return getNestedValue(locale, key)
}
